"use client";

import { usePropertyFilters, usePublicProperties } from "@/hooks/usePublicData";
import { getImageWithFallback } from "@/lib/default-image";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { Autoplay, FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

// Swiper CSS Styles
import "swiper/css";
import "swiper/css/navigation";

import { Icon } from "./Home";
import ImageCarousel from "@/components/ui/ImageCarousel";
import QuickBook from "@/components/properties/QuickBook";
import PriceTag from "@/components/ui/PriceTag";

export default function ExploreProperties({
  locale,
  isAr,
}: {
  locale: string;
  isAr: boolean;
}) {
  const t = (ar: string, en: string) => (isAr ? ar : en);

  const { data: filters = [] } = usePropertyFilters();
  const [activeFilter, setActiveFilter] = useState<string>("all");
  // "اختر" on a card → quick booking sheet
  const [quick, setQuick] = useState<{ id: string; title: string; image: string; unitCode?: string } | null>(null);

  const { data: propsRes, isLoading } = usePublicProperties({
    page: 1,
    limit: 12,
    filterId: activeFilter !== "all" ? activeFilter : undefined,
  });
  const properties = propsRes?.data ?? [];

  const chips = useMemo(
    () => [{ id: "all", name: { ar: "الكل", en: "All" } }, ...filters],
    [filters],
  );

  // استخراج أول 3 صور لعرضها بشكل طبقات متراكمة في كارت "See All"
  const previewImages = useMemo(() => {
    return properties
      .slice(0, 3)
      .map((p: any) => getImageWithFallback(p.images?.[0] ?? null, "property"));
  }, [properties]);

  return (
    <section className="py-12 px-4 sm:px-6 container mx-auto overflow-hidden">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div className="w-full md:w-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-clay-600 mb-4">
            {t("استكشف الوحدات", "Explore units")}
          </h2>

          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 max-w-full">
            {chips?.map((c: any) => {
              const id = c.id ?? c._id;
              const active = activeFilter === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveFilter(id)}
                  className={`shrink-0 px-5 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 cursor-pointer ${
                    active
                      ? "bg-clay-600 text-white shadow-md shadow-clay-600/20 scale-105"
                      : "bg-surface border border-clay-100 text-ink-900 hover:bg-clay-50 hover:border-clay-300"
                  }`}
                >
                  {isAr ? c.name?.ar : c.name?.en}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation & View All Link */}
        <div className="flex items-center gap-4 shrink-0 w-full md:w-auto justify-between md:justify-end border-t border-clay-100 pt-4 md:pt-0 md:border-none">
          <Link
            href={`/${locale}/explore${activeFilter !== "all" ? `?filterId=${activeFilter}` : ""}`}
            className="text-clay-600 font-bold text-sm flex items-center gap-2 hover:gap-3 transition-all group"
          >
            {t("عرض الكل", "View all")}
            <Icon
              name={isAr ? "arrow_back" : "arrow_forward"}
              className="text-[18px] transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
            />
          </Link>

          {/* Custom Swiper Buttons */}
          <div className="flex items-center gap-2">
            <button
              id="swiper-prev-btn"
              aria-label="Previous Slide"
              className="w-10 h-10 rounded-full border border-clay-200 flex items-center justify-center text-clay-600 hover:bg-clay-600 hover:text-white hover:border-clay-600 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon
                name={isAr ? "chevron_right" : "chevron_left"}
                className="text-[22px]"
              />
            </button>
            <button
              id="swiper-next-btn"
              aria-label="Next Slide"
              className="w-10 h-10 rounded-full border border-clay-200 flex items-center justify-center text-clay-600 hover:bg-clay-600 hover:text-white hover:border-clay-600 transition-all shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon
                name={isAr ? "chevron_left" : "chevron_right"}
                className="text-[22px]"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {isLoading ? (
        /* Loading Skeleton */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-3xl border border-clay-100 p-3 bg-surface shadow-sm"
            >
              <div className="aspect-16/10 rounded-2xl bg-clay-100/60 mb-4" />
              <div className="h-5 w-3/4 bg-clay-100/60 rounded-md mb-2" />
              <div className="h-4 w-1/2 bg-clay-100/60 rounded-md mb-4" />
              <div className="h-4 w-full bg-clay-100/40 rounded-md mb-3" />
              <div className="h-6 w-1/3 bg-clay-100/60 rounded-md" />
            </div>
          ))}
        </div>
      ) : properties.length === 0 ? (
        /* Empty State */
        <div className="text-center py-20 bg-surface/50 rounded-3xl border border-dashed border-clay-200">
          <Icon name="search_off" className="text-4xl text-ink-400 mb-2" />
          <p className="text-ink-600 font-medium">
            {t(
              "لا توجد وحدات متاحة في هذا التصنيف حالياً",
              "No properties available in this category",
            )}
          </p>
        </div>
      ) : (
        /* Swiper Carousel */
        <Swiper
          modules={[Navigation, Autoplay, FreeMode]}
          dir={isAr ? "rtl" : "ltr"}
          key={isAr ? "rtl-swiper" : "ltr-swiper"}
          spaceBetween={24}
          slidesPerView={1.15}
          freeMode={{ enabled: true, sticky: false }}
          navigation={{
            prevEl: "#swiper-prev-btn",
            nextEl: "#swiper-next-btn",
          }}
          autoplay={{
            delay: 4500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            540: { slidesPerView: 2, spaceBetween: 20 },
            768: { slidesPerView: 2.8, spaceBetween: 24 },
            1024: { slidesPerView: 3.8, spaceBetween: 24 },
            1280: { slidesPerView: 4.2, spaceBetween: 24 },
          }}
          className="py-4! px-1! -mx-1"
        >
          {properties.map((p: any) => {
            const pid = p.slug || p._id || p.id;
            const title = isAr ? p.title?.ar : p.title?.en;
            const loc =
              (isAr ? p.region?.ar : p.region?.en) || p.location?.address || "";
            const imgs: string[] = (p.images || []).filter(Boolean);

            return (
              <SwiperSlide key={p._id || p.id} className="h-auto">
                <Link
                  href={`/${locale}/explore/${pid}`}
                  className="group flex flex-col h-full bg-surface border border-clay-100/80 hover:border-clay-300 rounded-3xl p-3 transition-all duration-500 hover:shadow-2xl hover:shadow-clay-600/10 hover:-translate-y-1.5"
                >
                  {/* Image Container with Smooth Hover Zoom */}
                  <div className="relative aspect-16/10 rounded-2xl overflow-hidden mb-3.5 shadow-inner bg-clay-50">
                    {/* All photos — swipe inside the card (a nested Swiper: the row stays still) */}
                    <ImageCarousel
                      images={imgs}
                      alt={title || "Property"}
                      isAr={isAr}
                      className="h-full w-full"
                      imgClassName="transition-transform duration-700 ease-out group-hover:scale-110"
                      sizes="(max-width: 640px) 85vw, (max-width: 1024px) 40vw, 25vw"
                    />

                    {/* Gradient Overlay for Hover */}
                    <div className="absolute inset-0 bg-linear-to-t from-black/40 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Featured Badge */}
                    {p.featured && (
                      <div className="absolute top-3 inset-s-3 bg-white/90 backdrop-blur-md text-clay-600 px-3 py-1 rounded-full text-xs font-bold shadow-sm flex items-center gap-1 z-10">
                        <Icon
                          name="stars"
                          className="text-[14px] text-clay-600"
                        />
                        <span>{t("مميز", "Featured")}</span>
                      </div>
                    )}

                    {/* Public discount (dashboard «خصم عام») */}
                    {p.discount?.active && (
                      <div className="pointer-events-none absolute bottom-3 inset-s-3 z-10 rounded-full bg-red-600 px-2.5 py-1 text-xs font-extrabold text-white shadow-md">
                        {t(`خصم ${p.discount.percent}%`, `${p.discount.percent}% off`)}
                      </div>
                    )}

                  </div>

                  {/* Property Details */}
                  <div className="flex flex-col grow px-1">
                    {/* Title & Rating */}
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <h3 className="font-bold text-base text-ink-900 truncate group-hover:text-clay-600 transition-colors duration-300">
                        {title}
                      </h3>
                      {p.rating ? (
                        <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-md shrink-0">
                          <Icon
                            name="star"
                            fill
                            className="text-amber-500 text-[14px]"
                          />
                          <span className="text-xs font-bold text-amber-900">
                            {p.rating}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    {/* Unit code */}
                    {p.unitCode && (
                      <p className="mb-1">
                        <span dir="ltr" className="inline-block rounded-md bg-clay-50 border border-clay-100 px-1.5 py-px font-mono text-[10.5px] font-semibold tracking-wide text-clay-700">
                          {p.unitCode}
                        </span>
                      </p>
                    )}

                    {/* Location */}
                    {loc && (
                      <p className="text-xs text-ink-500 mb-3 truncate flex items-center gap-1">
                        <Icon
                          name="location_on"
                          className="text-[14px] shrink-0 text-clay-400"
                        />
                        <span className="truncate">{loc}</span>
                      </p>
                    )}

                    {/* Price */}
                    <div className="mt-auto pt-2 border-t border-clay-100/60 flex items-center justify-between gap-2">
                      <PriceTag p={p} isAr={isAr} showBadge={false} priceClassName="text-clay-600 font-black text-lg" />
                      {/* Book from the card — no need to open the unit page */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQuick({ id: pid, title, image: getImageWithFallback(imgs[0] ?? null, "property"), unitCode: p.unitCode });
                        }}
                        className="shrink-0 rounded-xl bg-clay-600 px-4 py-1.5 text-sm font-bold text-white shadow-sm transition hover:bg-clay-700 active:scale-[0.97]"
                      >
                        {t("اختر", "Select")}
                      </button>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            );
          })}

          {/* Special "See All" Card with Stacked Hover Animation */}
          <SwiperSlide className="h-auto">
            <Link
              href={`/${locale}/explore${activeFilter !== "all" ? `?filterId=${activeFilter}` : ""}`}
              className="group flex flex-col items-center justify-center h-full bg-surface border border-clay-100 hover:border-clay-300 rounded-3xl p-6 text-center cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-clay-600/10 hover:-translate-y-1.5 min-h-85"
            >
              {/* Stacked Images Showcase with Interactive Hover */}
              <div className="relative w-28 h-24 mb-6 flex items-center justify-center">
                {previewImages[2] && (
                  <div className="absolute w-16 h-20 rounded-xl overflow-hidden border-2 border-white shadow-md bg-clay-100 transform translate-x-4 -rotate-12 transition-all duration-500 ease-out group-hover:translate-x-7 group-hover:-rotate-18 group-hover:scale-105">
                    <Image
                      src={previewImages[2]}
                      alt="Preview"
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  </div>
                )}
                {previewImages[1] && (
                  <div className="absolute w-16 h-20 rounded-xl overflow-hidden border-2 border-white shadow-md bg-clay-100 transform -translate-x-4 rotate-12 transition-all duration-500 ease-out group-hover:-translate-x-7 group-hover:rotate-18 group-hover:scale-105">
                    <Image
                      src={previewImages[1]}
                      alt="Preview"
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  </div>
                )}
                {previewImages[0] && (
                  <div className="relative z-10 w-18 h-22 rounded-xl overflow-hidden border-2 border-white shadow-xl bg-clay-100 transition-all duration-500 ease-out group-hover:scale-110">
                    <Image
                      src={previewImages[0]}
                      alt="Preview"
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  </div>
                )}
              </div>

              {/* Action Button & Text */}
              <div className="flex items-center gap-2 font-bold text-clay-600 text-base group-hover:text-clay-700">
                <span>{t("استكشف المزيد", "Explore all")}</span>
                <div className="w-8 h-8 rounded-full bg-clay-50 group-hover:bg-clay-600 group-hover:text-white flex items-center justify-center transition-all duration-300">
                  <Icon
                    name={isAr ? "arrow_back" : "arrow_forward"}
                    className="text-[18px]"
                  />
                </div>
              </div>
              <p className="text-xs text-ink-400 mt-1">
                {t("عرض كافة الخيارات المتاحة", "View all properties")}
              </p>
            </Link>
          </SwiperSlide>
        </Swiper>
      )}
      {quick && (
        <QuickBook
          propertyId={quick.id}
          isAr={isAr}
          preview={{ title: quick.title, image: quick.image, unitCode: quick.unitCode }}
          onClose={() => setQuick(null)}
        />
      )}
    </section>
  );
}
